import type { V2 } from '../core/math';

/**
 * Named camera viewpoints for screenshots, reviews and cinematic beats. While a viewpoint is active
 * the render system places the camera itself (after the gameplay rig), and lighting/shadows/fog
 * follow the viewpoint target.
 */
export interface Viewpoint {
  target: V2;
  /** Distance from target along the view ray (m). */
  distance: number;
  /** Elevation in degrees (90 = straight down). */
  pitchDeg: number;
  /** Heading of "screen up" offset from north, degrees. */
  yawDeg: number;
  fov?: number;
  /** Fog density override (overview shots want less). */
  fog?: number;
}

export const VIEWPOINTS: Record<string, Viewpoint> = {
  overview: { target: { x: 0, z: 18 }, distance: 1050, pitchDeg: 62, yawDeg: 0, fov: 34, fog: 0.00028 },
  spawn: { target: { x: 12, z: -74 }, distance: 62, pitchDeg: 58, yawDeg: 0 },
  parade: { target: { x: 4, z: -128 }, distance: 92, pitchDeg: 55, yawDeg: -8 },
  'tanners-row': { target: { x: 150, z: -168 }, distance: 96, pitchDeg: 56, yawDeg: 6 },
  'cinder-yards': { target: { x: -118, z: -170 }, distance: 110, pitchDeg: 52, yawDeg: -12 },
  'gullet-basin': { target: { x: 150, z: 205 }, distance: 120, pitchDeg: 50, yawDeg: 10 },
  'mercy-square': { target: { x: 172, z: 14 }, distance: 105, pitchDeg: 55, yawDeg: 0 },
  canal: { target: { x: -40, z: 160 }, distance: 100, pitchDeg: 50, yawDeg: -6 },
  viaduct: { target: { x: -128, z: 36 }, distance: 70, pitchDeg: 48, yawDeg: 18 },
  'kiln-common': { target: { x: 40, z: -360 }, distance: 115, pitchDeg: 54, yawDeg: 0 },};

/** Shared, mutable: which viewpoint (if any) is active. */
export const viewState: { active: Viewpoint | null; name: string | null } = { active: null, name: null };

export function viewpointFocus(): V2 | null {
  return viewState.active ? viewState.active.target : null;
}
